import { UpdateResolver } from '@urql/exchange-graphcache';
import { gql } from 'urql';
import {
  CreateHistoryEventMutation,
  CreateHistoryEventMutationVariables,
  UpdateHistoryEventMutation,
  UpdateHistoryEventMutationVariables,
  HistoryEventDeepFragment,
} from '../../gql/graphql';

export const createHistoryEventResolver: UpdateResolver = (
  result: CreateHistoryEventMutation,
  args: CreateHistoryEventMutationVariables,
  cache
) => {
  const newEvent = result.createHistoryEvent?.historyEvent;
  if (!newEvent) return;

  const allFields = cache.inspectFields('Query');
  const matching = allFields.filter(
    (field) =>
      field.fieldName === 'historyEventsConnection' &&
      (field.arguments?.condition as any)?.projectId === newEvent.projectId
  );

  for (const field of matching) {
    cache.invalidate('Query', 'historyEventsConnection', field.arguments);
  }

  // Invalidate the events groups cache as well.
  const eventGroupsMatching = allFields.filter(
    (field) =>
      field.fieldName === 'getHistoryEventGroups' &&
      (field.arguments as any)?.projectId === newEvent.projectId
  );
  for (const field of eventGroupsMatching) {
    cache.invalidate('Query', 'getHistoryEventGroups', field.arguments);
  }
};

export const updateHistoryEventResolver: UpdateResolver = (
  result: UpdateHistoryEventMutation,
  args: UpdateHistoryEventMutationVariables,
  cache
) => {
  if (result.updateHistoryEvent?.historyEvent) {
    // Get the existing event from the cache
    const existingEvent = cache.resolve(
      {
        __typename: 'HistoryEvent',
        id: result.updateHistoryEvent.historyEvent.id,
      },
      'id'
    ) as HistoryEventDeepFragment | null;

    if (existingEvent) {
      // Merge the update with the existing event
      const updatedEvent = {
        ...existingEvent,
        ...result.updateHistoryEvent.historyEvent,
      };

      // Write the updated event to the cache
      cache.writeFragment(
        gql`
          fragment _ on HistoryEvent {
            id
            projectId
            author
            createdAt
            updatedAt
            eventName
            eventType
            resourceName
            resourceType
            resolved
            stateKey
            metadata
          }
        `,
        updatedEvent
      );

      // The connection will automatically update since we're using normalized data
      // No need to manually update the connection
    }

    // Invalidate the events groups cache.
    const allFields = cache.inspectFields('Query');
    const eventGroupsMatching = allFields.filter(
      (field) =>
        field.fieldName === 'getHistoryEventGroups' &&
        (field.arguments as any)?.projectId ===
          result.updateHistoryEvent?.historyEvent?.projectId
    );
    for (const field of eventGroupsMatching) {
      cache.invalidate('Query', 'getHistoryEventGroups', field.arguments);
    }
  }
};
